import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Link,
  IconButton,
  VStack,
  Heading,
  Button,
  useColorModeValue,
  Container,
  Badge,
  Image,
  Spacer,
  Tooltip,
  useBreakpointValue,
  Text,
  HStack,
  Portal,
  Stack,
  CloseButton,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import CalendlyButton from '../utils/CalendlyButton';
import ContactButton from '../utils/ContactButton';
import logoRF from '../utils/images/logo.png';

const Header = () => {
  // Replace useDisclosure with a simple local state to avoid version/API issues
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMobileSubMenu, setOpenMobileSubMenu] = useState(null);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const portalRef = useRef(null);

  const handleMobileMenuClick = () => {
    const next = !isMenuOpen;
    console.log('Mobile menu clicked!', { isMenuOpen: next });
    setIsMenuOpen(next);
  };

  const contact = {
    email: process.env.REACT_APP_EMAIL || 'contact@groupe-solarenov.com',
  };

  const services = [
    { name: 'Première demande de titre de séjour', href: '/premiere-demande' },
    { name: 'Renouvellement de titre de séjour', href: '/renouvellement' },
    { name: 'Demande de visa', href: '/demande-visa' },
    { name: 'OQTF', href: '/oqtf' },
    { name: 'Contestation d\'une décision administrative', href: '/contestation' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleToggleSubMenu = (itemPath) => {
    setOpenMobileSubMenu(openMobileSubMenu === itemPath ? null : itemPath);
  };

  return (
    <Box 
      position="fixed" 
      top={0} 
      width="100%" 
      zIndex={1000}
      transition="all 0.3s"
    >
      {/* Bandeau supérieur */}
      <Box bg="linear-gradient(90deg, #094c26, #0ea5e9)" py={{ base: 1, md: 2 }}>
        <Container maxW="1200px" px={{ base: 3, md: 6 }}>
          <Flex justify="space-between" align="center" flexWrap="nowrap" gap={{ base: 1, md: 3 }}>
            <HStack spacing={{ base: 1, md: 3 }}>
              <Text fontSize={{ base: 'xs', md: 'sm' }} color="white" fontWeight="bold">🌱</Text>
              <Text fontSize={{ base: 'xs', md: 'sm' }} color="white" fontWeight="bold">Aides jusqu'à 90%</Text>
            </HStack>
            <HStack spacing={{ base: 3, md: 6 }}>
              <Link 
                href={`mailto:${contact.email}`} 
                display={{ base: 'none', md: 'inline' }}
                fontSize={{ base: 'xs', md: 'sm' }} 
                color="white" 
                fontWeight="bold"
                _hover={{ opacity: 0.9, transform: "scale(1.02)" }}
                transition="all 0.2s"
              >
                ✉️ {contact.email}
              </Link>
              <ContactButton 
                size="xs"
                fontSize={{ base: 'xs', md: 'sm' }}
                px={{ base: 2, md: 3 }}
                py={{ base: 1, md: 2 }}
                h={{ base: '24px', md: '28px' }}
              />
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Navigation principale */}
      <motion.div
        initial={{ y: -100 }}
        animate={{ 
          y: 0,
          backgroundColor: isScrolled ? "rgba(255, 255, 255, 0.95)" : "white",
          backdropFilter: isScrolled ? "blur(10px)" : "none",
          boxShadow: isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.08)" : "0 2px 10px rgba(0, 0, 0, 0.1)",
        }}
        transition={{ duration: 0.4 }}
      >
        <Box as="nav" py={{ base: 2, lg: 1 }}>
          <Container maxW="1200px" px={6}>
          <Flex justify="space-between" align="center" w="100%">
              {/* Logo */}
            <Box flex="1">
              <Link href="/" textDecoration="none" _hover={{ textDecoration: "none" }}>
                <Image
                  src={logoRF}
                  alt="Groupe Solarenov"
                  h={{ base: '60px', md: '84px' }}
                  objectFit="contain"
                />
              </Link>
            </Box>

            {/* Navigation desktop - centrée */}
            <Flex justify="center" align="center" flex="2" display={{ base: 'none', lg: 'flex' }}>
              <HStack spacing={12}>
                <Link 
                  href="/" 
                  color="gray.700" 
                  fontWeight="bold" 
                  fontSize="md"
                  _hover={{ color: '#0053b3', transform: "translateY(-1px)" }}
                  transition="all 0.2s"
                  textAlign="center"
                  py={4}
                  px={6}
                  borderRadius="lg"
                  _focus={{ bg: 'gray.50' }}
                  _active={{ bg: 'gray.100' }}
                >
                  Accueil
              </Link>
                <Link 
                  href="/a-propos" 
                  color="gray.700" 
                  fontWeight="bold" 
                  fontSize="md"
                  _hover={{ color: '#0053b3', transform: "translateY(-1px)" }}
                  transition="all 0.2s"
                  textAlign="center"
                  py={4}
                  px={6}
                  borderRadius="lg"
                  _focus={{ bg: 'gray.50' }}
                  _active={{ bg: 'gray.100' }}
                >
                  À propos
                </Link>
                
                {/* Menu Services personnalisé */}
                {/* <Box position="relative" onMouseEnter={() => setIsServicesOpen(true)} onMouseLeave={() => setIsServicesOpen(false)}>
                  <Button
                    variant="ghost"
                    py={4}
                    px={6}
                    fontWeight="bold"
                    fontSize="md"
                    color="gray.700"
                    _hover={{ color: '#0053b3', transform: "translateY(-1px)", bg: 'gray.50' }}
                    _active={{ bg: "gray.100" }}
                    transition="all 0.2s ease"
                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                    borderRadius="lg"
                    textAlign="center"
                    minH="48px"
                  >
                    <HStack spacing={2} align="center">
                      <Text>Services</Text>
                      <Box as="span" display="inline-block" w="16px" h="16px" transition="transform 0.2s ease" transform={isServicesOpen ? 'rotate(180deg)' : 'rotate(0deg)'} color={isServicesOpen ? '#0053b3' : 'gray.500'}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <path d="M7 10l5 5 5-5z" />
                        </svg>
                      </Box>
                    </HStack>
                  </Button>
                  {isServicesOpen && (
                    <Box
                      position="absolute"
                      top="100%"
                      left="50%"
                      transform="translateX(-50%)"
                      bg="white"
                      boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                      rounded="2xl"
                      py={4}
                      overflow="hidden"
                      mt={2}
                      border="1px solid"
                      borderColor="gray.100"
                      width="350px"
                      zIndex={1000}
                      onMouseEnter={() => setIsServicesOpen(true)}
                      onMouseLeave={() => setIsServicesOpen(false)}
                    >
                      <Box px={5} pb={3} mb={3} borderBottom="2px solid" borderColor="gray.100">
                        <Text fontWeight="bold" fontSize="sm" color="#0053b3" textTransform="uppercase" letterSpacing="wider" textAlign="center">
                          Nos Services
                        </Text>
                      </Box>
                      {services.map((service, index) => (
                        <Link
                          key={index}
                          href={service.href}
                          display="block"
                          py={3}
                          px={5}
                          cursor="pointer"
                          _hover={{ bg: '#f8fafc', transform: "translateX(4px)" }}
                          _focus={{ bg: '#f8fafc' }}
                          borderLeft="4px solid transparent"
                          _active={{ borderLeftColor: '#0053b3' }}
                          transition="all 0.2s"
                          textDecoration="none"
                          borderRadius="lg"
                          mx={2}
                        >
                          <Text fontSize="sm" fontWeight="bold" color="gray.700" textAlign="center">
                            {service.name}
                          </Text>
              </Link>
                      ))}
                    </Box>
                  )}
                </Box> */}
                
                <Link 
                  href="/contact" 
                  color="gray.700" 
                  fontWeight="bold" 
                  fontSize="md"
                  _hover={{ color: '#0053b3', transform: "translateY(-1px)" }}
                  transition="all 0.2s"
                  textAlign="center"
                  py={4}
                  px={6}
                  borderRadius="lg"
                  _focus={{ bg: 'gray.50' }}
                  _active={{ bg: 'gray.100' }}
                >
                  Contact
              </Link>
              </HStack>
            </Flex>

            {/* Bouton CTA - aligné à droite */}
            <Box flex="1" display={{ base: 'none', lg: 'flex' }} justify="flex-end">
              <ContactButton />
            </Box>

            {/* Bouton menu mobile */}
            <Button
              display={{ base: 'flex', lg: 'none' }}
              onClick={handleMobileMenuClick}
              variant="outline"
              borderRadius="lg"
              bg="white"
              border="2px solid"
              borderColor="#0053b3"
              color="#0053b3"
              size="lg"
              p={4}
              minW="60px"
              h="50px"
              _hover={{ 
                bg: '#0053b3', 
                color: "white",
                transform: "scale(1.05)",
                borderColor: "#0053b3"
              }}
              _active={{
                bg: '#003d85',
                transform: "scale(0.95)"
              }}
              transition="all 0.2s"
              boxShadow="0 4px 12px rgba(0, 83, 179, 0.3)"
              fontWeight="bold"
              fontSize="lg"
            >
              ☰
            </Button>
            </Flex>
          </Container>
        </Box>
      </motion.div>

      {/* Mobile Menu - Custom implementation */}
      {isMenuOpen && (
        <Portal>
          {/* Backdrop */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0, 0, 0, 0.5)"
            zIndex={9999}
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Menu content */}
          <Box
            position="fixed"
            top={0}
            right={0}
            bottom={0}
            width="320px"
            bg="white"
            zIndex={10000}
            boxShadow="2xl"
            overflowY="auto"
            border="2px solid"
            borderColor="#0053b3"
          >
            {/* Header */}
            <Box p={6} borderBottomWidth="2px" borderColor="gray.100">
              <Flex justify="space-between" align="center">
                <Image
                  src={logoRF}
                  alt="Groupe Solarenov"
                  h="64px"
                  objectFit="contain"
                />
                <CloseButton 
                  onClick={() => setIsMenuOpen(false)} 
                  size="md"
                  borderRadius="full"
                  _hover={{ bg: 'gray.100', transform: "scale(1.1)" }}
                  transition="all 0.2s"
            />
          </Flex>
            </Box>
            
            {/* Body */}
            <Box p={3}>
              <VStack 
                spacing={2}
                align="stretch"
                py={2}
              >
                <Button
                  variant="ghost"
                  justifyContent="center"
                  href="/"
                  as={Link}
                  fontWeight="bold"
                  fontSize="lg"
                  px={6}
                  py={4}
                  borderRadius="xl"
                  color="gray.700"
                  _hover={{ bg: 'gray.50', transform: "scale(1.02)" }}
                  transition="all 0.2s"
                  width="100%"
                  borderBottom="none"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Accueil
                </Button>
                <Button
                  variant="ghost"
                  justifyContent="center"
                  href="/a-propos"
                  as={Link}
                  fontWeight="bold"
                  fontSize="lg"
                  px={6}
                  py={4}
                  borderRadius="xl"
                  color="gray.700"
                  _hover={{ bg: 'gray.50', transform: "scale(1.02)" }}
                  transition="all 0.2s"
                  width="100%"
                  borderBottom="none"
                  onClick={() => setIsMenuOpen(false)}
                >
                  À propos
                </Button>
                
                {/* Services mobile avec sous-menu */}
                {/* <VStack align="stretch" spacing={2}>
                  <Button
                    variant="ghost"
                    justifyContent="center"
                    alignItems="center"
                    onClick={() => handleToggleSubMenu('services')}
                    fontWeight="bold"
                    fontSize="lg"
                    px={6}
                    py={4}
                    borderRadius="xl"
                    color="gray.700"
                    _hover={{ bg: 'gray.50', transform: "scale(1.02)" }}
                    transition="all 0.2s"
                    width="100%"
                    borderBottom="none"
                  >
                    <HStack spacing={2} align="center" justify="center" w="100%">
                      <Text>Services</Text>
                      <Box as="span" display="inline-block" w="20px" h="20px" transition="transform 0.2s ease" transform={openMobileSubMenu === 'services' ? 'rotate(180deg)' : 'rotate(0deg)'}>
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" style={{ color: 'gray', color: 'inherit' }}>
                          <path d="M7 10l5 5 5-5z" />
                        </svg>
                      </Box>
                    </HStack>
                  </Button>
                  {openMobileSubMenu === 'services' && (
                    <VStack 
                      align="stretch" 
                      spacing={2}
                      bg="gray.50"
                      borderRadius="xl"
                      py={3}
                      px={2}
                      boxShadow="none"
                      border="none"
                      mx={0}
                      width="100%"
                      mt={2}
                    >
                          {services.map((service, index) => (
                            <Button
                              key={index}
                              variant="ghost"
                              justifyContent="center"
                              as={Link}
                              href={service.href}
                              py={3}
                              px={4}
                              borderRadius="lg"
                              color="gray.700"
                              borderLeft="none"
                              _hover={{ bg: "white", transform: "scale(1.02)" }}
                              transition="all 0.2s"
                              fontWeight="bold"
                              height="auto"
                              width="100%"
                              mt={0}
                              borderBottom="none"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <Text fontSize="sm" fontWeight="bold" textAlign="center">
                                {service.name}
                              </Text>
                            </Button>
                          ))}
                    </VStack>
                  )}
                </VStack> */}
                
                <Button
                  variant="ghost"
                  justifyContent="center"
                  href="/contact"
                  as={Link}
                  fontWeight="bold"
                  fontSize="lg"
                  px={6}
                  py={4}
                  borderRadius="xl"
                  color="gray.700"
                  _hover={{ bg: 'gray.50', transform: "scale(1.02)" }}
                  transition="all 0.2s"
                  width="100%"
                  borderBottom="none"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact
                </Button>
                
                <Box p={4}>
                  <ContactButton 
                    size="lg"
                    w="100%"
                    py={6}
                    fontSize="lg"
                    onClick={() => setIsMenuOpen(false)}
                  />
                </Box>
            </VStack>
            </Box>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default Header;
